import heic2any from 'heic2any';
import imageCompression from 'browser-image-compression';

/**
 * Maximum file size in MB for uploads
 */
export const MAX_FILE_SIZE_MB = 8;

/**
 * Check if file is a HEIC/HEIF format (iPhone RAW)
 */
export const isHeicFormat = (file) => {
  const heicTypes = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
  const heicExtensions = ['.heic', '.heif'];
  
  if (heicTypes.includes(file.type?.toLowerCase())) {
    return true;
  }
  
  const fileName = file.name?.toLowerCase() || '';
  return heicExtensions.some(ext => fileName.endsWith(ext));
};

/**
 * Convert HEIC/HEIF to JPEG
 */
export const convertHeicToJpeg = async (file) => {
  try {
    console.log('Starting HEIC conversion for:', file.name, 'Size:', file.size);
    
    const blob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9
    });
    
    // heic2any can return an array for multi-image HEIC files
    const resultBlob = Array.isArray(blob) ? blob[0] : blob;
    
    // Create a new file with .jpg extension
    const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    const convertedFile = new File([resultBlob], newFileName, { type: 'image/jpeg' });
    
    console.log('HEIC conversion successful:', newFileName, 'New size:', convertedFile.size);
    return convertedFile;
  } catch (error) {
    console.error('HEIC conversion error:', error);
    // Return the original file if conversion fails - backend might still accept it
    console.log('HEIC conversion failed, returning original file');
    throw new Error('Failed to convert HEIC image. Please try a different image format or convert to JPG first.');
  }
};

/**
 * Compress image to target size (in MB)
 */
export const compressImage = async (file, maxSizeMB = MAX_FILE_SIZE_MB, onProgress = null) => {
  const options = {
    maxSizeMB: maxSizeMB,
    maxWidthOrHeight: 2048, // Reduced from 4096 to ensure smaller files
    useWebWorker: true,
    fileType: 'image/jpeg', // Always convert to JPEG for smaller size
    initialQuality: 0.8,
    onProgress: onProgress
  };

  try {
    console.log(`[COMPRESS] Starting compression: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    const compressedFile = await imageCompression(file, options);
    console.log(`[COMPRESS] Compressed to: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
    return compressedFile;
  } catch (error) {
    console.error('Compression error:', error);
    throw new Error('Failed to compress image. Please try a smaller image.');
  }
};

/**
 * Process image for upload - handles HEIC conversion and compression
 * @param {File} file - The image file to process
 * @param {number} maxSizeMB - Maximum file size in MB (default: 8)
 * @param {function} onProgress - Progress callback (optional)
 * @returns {Promise<File>} - Processed file ready for upload
 */
export const processImageForUpload = async (file, maxSizeMB = MAX_FILE_SIZE_MB, onProgress = null) => {
  let processedFile = file;
  
  // Step 1: Convert HEIC to JPEG if needed
  if (isHeicFormat(file)) {
    if (onProgress) onProgress({ stage: 'converting', progress: 0 });
    processedFile = await convertHeicToJpeg(file);
    if (onProgress) onProgress({ stage: 'converting', progress: 100 });
  }
  
  // Step 2: Always compress images to ensure they're under the limit
  // This helps prevent network errors from large file uploads
  const fileSizeMB = processedFile.size / (1024 * 1024);
  
  // Compress if over 2MB OR if it's a large dimension image (even if under size limit)
  if (fileSizeMB > 2) {
    if (onProgress) onProgress({ stage: 'compressing', progress: 0 });
    
    const compressionProgress = (progress) => {
      if (onProgress) onProgress({ stage: 'compressing', progress: Math.round(progress) });
    };
    
    processedFile = await compressImage(processedFile, maxSizeMB, compressionProgress);
    
    if (onProgress) onProgress({ stage: 'compressing', progress: 100 });
  }
  
  return processedFile;
};

/**
 * Validate image file type
 */
export const isValidImageType = (file) => {
  const validTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/gif',
    'image/webp',
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence'
  ];
  
  const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];
  
  if (validTypes.includes(file.type?.toLowerCase())) {
    return true;
  }
  
  const fileName = file.name?.toLowerCase() || '';
  return validExtensions.some(ext => fileName.endsWith(ext));
};

/**
 * Get human-readable file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Accepted file types string for input accept attribute
 */
export const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif,.jpg,.jpeg,.png,.gif,.webp';
